import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress
} from "@mui/material";
import { ShoppingCart, Upload, Refresh, Assessment } from "@mui/icons-material";
import axios from "axios";

// Componentes de pasos
import PasoSubirXML from "./compras/pasos/PasoSubirXML";
import PasoConfirmarCompra from "./compras/pasos/PasoConfirmarCompra";
import PasoExito from "./compras/pasos/PasoExito";
import TablaCompras from "./compras/TablaCompras";

const API_URL = process.env.REACT_APP_API_URL || "";

const steps = ["Subir XML", "Confirmar Compra", "Completado"];

export default function ComprasSimplificado() {
  // Estados principales
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  // Estados del proceso
  const [xmlFile, setXmlFile] = useState(null);
  const [facturaParseada, setFacturaParseada] = useState(null);
  const [retenciones, setRetenciones] = useState(null);
  const [compraCreada, setCompraCreada] = useState(null);
  
  // Estados para el formulario
  const [cuentas, setCuentas] = useState([]);
  const [productos, setProductos] = useState([]);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    cargarCompras();
    cargarCuentas();
    cargarProductos();
  }, []);

  const cargarCompras = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/contabilidad/compras`, {
        headers: getAuthHeaders()
      });
      setCompras(res.data);
    } catch (error) {
      showSnackbar("Error al cargar compras", "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarCuentas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, {
        headers: getAuthHeaders()
      });
      const cuentasMovimiento = res.data.filter(c => c.acepta_movimiento);
      setCuentas(cuentasMovimiento);
    } catch (error) {
      console.error("Error al cargar cuentas:", error);
    }
  };

  const cargarProductos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/productos`, {
        headers: getAuthHeaders()
      });
      setProductos(res.data);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      setProductos([]);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleNuevaCompra = () => {
    setDialogOpen(true);
    setActiveStep(0);
    setXmlFile(null);
    setFacturaParseada(null);
    setRetenciones(null);
    setCompraCreada(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setActiveStep(0);
    setXmlFile(null);
    setFacturaParseada(null);
    setRetenciones(null);
    setCompraCreada(null);
  };

  const handleFileChange = (file) => {
    if (file && file.name.endsWith(".xml")) {
      setXmlFile(file);
      showSnackbar(`Archivo XML seleccionado: ${file.name}`, "success");
    } else {
      showSnackbar("Por favor seleccione un archivo XML válido", "error");
    }
  };

  const handleParsearXML = async () => {
    if (!xmlFile) {
      showSnackbar("Seleccione un archivo XML", "error");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("xml", xmlFile);

    try {
      const res = await axios.post(
        `${API_URL}/api/contabilidad/compras/parsear-xml`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      setFacturaParseada(res.data.factura);
      setRetenciones(res.data.retenciones);
      setActiveStep(1); // Ir al paso de confirmar
      showSnackbar("XML parseado correctamente", "success");
    } catch (error) {
      if (error.response?.data?.duplicada) {
        const confirmar = window.confirm(
          `${error.response.data.error}\n\n¿Desea registrar esta factura de nuevo?`
        );
        if (confirmar) {
          // Continuar con duplicado permitido
          setFacturaParseada({ ...error.response.data.factura, permitirDuplicado: true });
          setActiveStep(1);
        }
      } else {
        showSnackbar(error.response?.data?.error || "Error al parsear XML", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarCompra = async (datosCompra) => {
    setLoading(true);
    
    try {
      const payload = {
        facturaData: facturaParseada,
        ...datosCompra,
        permitirDuplicado: facturaParseada.permitirDuplicado || false
      };

      const res = await axios.post(
        `${API_URL}/api/contabilidad/compras/con-asiento`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      setCompraCreada(res.data.compra);
      setActiveStep(2); // Ir al paso de éxito
      showSnackbar(res.data.message || "Compra registrada exitosamente", "success");
      
      // Recargar compras
      cargarCompras();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.codigo === "CLAVE_DUPLICADA") {
        const confirmar = window.confirm(
          `${error.response.data.error}\n\n¿Desea registrar la compra de todas formas?`
        );
        
        if (confirmar) {
          // Reintentar con duplicado permitido directamente
          try {
            const payloadRetry = {
              facturaData: facturaParseada,
              ...datosCompra,
              permitirDuplicado: true
            };
            const res = await axios.post(
              `${API_URL}/api/contabilidad/compras/con-asiento`,
              payloadRetry,
              { headers: getAuthHeaders() }
            );
            setCompraCreada(res.data.compra);
            setActiveStep(2);
            showSnackbar(res.data.message || "Compra registrada exitosamente", "success");
            cargarCompras();
          } catch (retryError) {
            showSnackbar(retryError.response?.data?.error || "Error al registrar compra", "error");
          }
        }
      } else {
        showSnackbar(error.response?.data?.error || "Error al registrar compra", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = () => {
    handleCloseDialog();
    cargarCompras();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PasoSubirXML
            xmlFile={xmlFile}
            onFileChange={handleFileChange}
            onParsear={handleParsearXML}
            loading={loading}
          />
        );
      
      case 1:
        return (
          <PasoConfirmarCompra
            factura={facturaParseada}
            retenciones={retenciones}
            cuentas={cuentas}
            productos={productos}
            onConfirmar={handleConfirmarCompra}
            onCancelar={() => setActiveStep(0)}
            loading={loading}
          />
        );
      
      case 2:
        return (
          <PasoExito
            compra={compraCreada}
            onFinalizar={handleFinalizar}
            onNuevaCompra={() => {
              setActiveStep(0);
              setXmlFile(null);
              setFacturaParseada(null);
              setCompraCreada(null);
            }}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingCart /> Módulo de Compras
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={cargarCompras}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleNuevaCompra}
            size="large"
          >
            Nueva Compra (XML)
          </Button>
        </Box>
      </Box>

      {/* Tabla de compras */}
      <Paper sx={{ p: 3 }}>
        <TablaCompras
          compras={compras}
          loading={loading}
          onRecargar={cargarCompras}
        />
      </Paper>

      {/* Dialog del wizard */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        disableEscapeKeyDown={loading}
        PaperProps={{ sx: { minHeight: "80vh" } }}
      >
        <DialogTitle>
          <Typography variant="h5">Registrar Nueva Compra</Typography>
        </DialogTitle>
        
        <DialogContent dividers sx={{ px: 4 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Contenido del paso */}
          {renderStepContent()}
        </DialogContent>

        {activeStep < 2 && (
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancelar
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}
